import { useAuth } from "../contexts/AuthContext";
import { useProjects } from "../hooks/useProjects";
import { Link } from "react-router-dom";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Skeleton } from "./ui/skeleton";

export default function Dashboard() {
  const { currentUser } = useAuth();
  const { projects, loading, error } = useProjects();

  if (error) return <div>Error: {error.message}</div>;

  return (
    <div className="container-elegant py-24">
        <div className="flex justify-between items-center mb-12">
          <h2 className="text-3xl font-light tracking-tight text-primary-950 dark:text-white">
            PROJETOS
          </h2>
          {currentUser?.role === 'ADMIN' && (
            <Button asChild>
              <Link to="/projects/new">Novo Projeto</Link>
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            // Skeleton loading
            Array.from({ length: 6 }).map((_, index) => (
              <Card key={index}>
                <CardContent className="p-8 space-y-3">
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-6 w-20" />
                </CardContent>
              </Card>
            ))
          ) : (
            projects.map((project: any) => (
              <Link
                key={project.id}
                to={`/projects/${project.id}`}
                className="bg-white dark:bg-primary-900 border border-primary-200 dark:border-primary-800 p-8 hover:border-primary-400 dark:hover:border-primary-600 transition-colors duration-300"
              >
                <h3 className="text-xl font-light tracking-tight text-primary-950 dark:text-white mb-2">
                  {project.name}
                </h3>
                {project.address && (
                  <p className="text-sm text-primary-600 dark:text-primary-400 mb-4">
                    {project.address}
                  </p>
                )}
                <div className="flex items-center gap-2">
                  <Badge variant={getStatusVariant(project.status)}>
                    {getStatusLabel(project.status)}
                  </Badge>
                </div>
              </Link>
            ))
          )}
        </div>

        {projects.length === 0 && (
          <div className="text-center py-24">
            <p className="text-primary-600 dark:text-primary-400 mb-6">
              Nenhum projeto ainda
            </p>
            {currentUser?.role === 'ADMIN' && (
              <Button asChild>
                <Link to="/projects/new">Criar Seu Primeiro Projeto</Link>
              </Button>
            )}
          </div>
        )}
    </div>
  );
}

function getStatusLabel(status: string) {
  const labels = {
    PLANNING: 'Planejamento',
    IN_PROGRESS: 'Em Andamento',
    COMPLETED: 'Concluído',
    ON_HOLD: 'Em Espera',
    CANCELLED: 'Cancelado',
  };
  return labels[status as keyof typeof labels] || status?.replace('_', ' ') || 'Desconhecido';
}

function getStatusVariant(status: string): "info" | "success" | "default" | "warning" | "destructive" {
  const variants: Record<string, "info" | "success" | "default" | "warning" | "destructive"> = {
    planning: 'info',
    in_progress: 'success',
    completed: 'default',
    on_hold: 'warning',
    cancelled: 'destructive',
  };
  return variants[status as keyof typeof variants] || 'info';
}
